import React, { useRef, useMemo, useCallback, useEffect, useState } from "react";
import { Sprite, Graphics, Container, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { getNewUITexture } from "../../../../assets";
import {
  selectFeatureSpinComplete,
  selectFeatureSpinActive,
  selectCriticalAnimationPlaying,
  selectPotCollectionRespinsLeft,
} from "../../../../State/selectors";
import { selectPotCollectionTransitionActive } from "../../../../State/Slices/animationsSlice";

import { usePotCollectionControl } from "../../../../Contexts/PotCollectionControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import store from "../../../../State/Store/store";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import anime from "animejs";


// ── Config ──────────────────────────────────────────────────────────────────
// Matches secondary ButtonPlay sizing exactly
const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  // An iPad upright (layout TABLET_PORTRAIT on the MOBILE device, 2026-10-29):
  // the spin button a touch smaller, as the platform's own games draw it.
  LAYOUT_CONFIGS: {
    // TABLET_PORTRAIT: { deviceMultiplier: 1.95 },
    // The base ButtonPlay's (2026-10-29, matched on their ruler).
    TABLET_PORTRAIT: { deviceMultiplier: 2.07, gapAboveToolbar: 0.1 },
  },
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.45 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 2.175 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.375 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 2.25 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.675 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.0 },
  },
};

// Minimum gap between actions (ms)
const MIN_ACTION_GAP = 50;
let lastActionTime = 0;

/**
 * ButtonPotCollectionPlay
 *
 * Play/Stop button shown during the Pot Collection bonus feature.
 * Replaces the base game play button (ButtonPlay hides via holdAndWin/potCollection gate).
 *
 * Visual: secondary theme (position, hover brightness filter, Graphics hit area)
 * Mechanics: RC pattern (simple play/stop toggle, spacebar support, manual stop)
 * No autoplay, no turbo hold — just click to spin, click to stop.
 */
const ButtonPotCollectionPlay = ({ toolbar }) => {
  const isHoveredRef = useRef(false);
  const spacePressedRef = useRef(false);

  // Shared hover/press brightness hook (replaces deprecated ColorMatrixFilter)
  const { buttonRef: brightnessRef, handlePointerOver: brightnessOver, handlePointerOut: brightnessOut, cleanupFilters } =
    useButtonBrightness();

  // Track whether a stop has been dispatched (button disabled until spin finishes)
  const [stopDispatched, setStopDispatched] = useState(false);

  // Respin counter display state — stored in React state to survive re-renders on resize
  const [counterValue, setCounterValue] = useState("0");
  const [counterVisible, setCounterVisible] = useState(false);

  const { playButtonRef, pcPlayCounterRef } = useReelRefs();
  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig, isMobileSmall } =
    useDimensions();
  const { handleManualStop, requestSlamStop, isFeatureAnimating, startAutoplay, autoplayStartedRef } = usePotCollectionControl();

  const potCollectionTransitionActive = useSelector(selectPotCollectionTransitionActive);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const respinsLeft = useSelector(selectPotCollectionRespinsLeft);

  const hasRespinsRemaining = respinsLeft > 0;

  // Button is interactive when:
  // - Not in transition
  // - AND either ready for spin (featureSpinComplete + respins left) or spinning (for manual stop)
  //   or feature animations playing (for slam stop)
  // - AND stop hasn't been dispatched yet
  const canManualStop = hasRespinsRemaining && featureSpinActive && !featureSpinComplete && !stopDispatched;
  const canSlamStop = hasRespinsRemaining && featureSpinActive && !featureSpinComplete;
  // Slam stop during EOB collection: featureSpinComplete=true but collection is running
  const canSlamStopCollection = featureSpinComplete && isFeatureAnimating();
  // First spin only — blocked entirely when bonus is done (respinsLeft === 0)
  const canStartAutoplay =
    hasRespinsRemaining &&
    featureSpinComplete &&
    !featureSpinActive &&
    !autoplayStartedRef.current;
  const isInteractive =
    !potCollectionTransitionActive &&
    !criticalAnimationPlaying &&
    (canManualStop || canSlamStop || canSlamStopCollection || canStartAutoplay);

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  // Reset stopDispatched when spin completes (ready for next spin)
  useEffect(() => {
    if (featureSpinComplete && !featureSpinActive) {
      setStopDispatched(false);
    }
  }, [featureSpinComplete, featureSpinActive]);

  // ── Position (matches secondary ButtonPlay exactly) ───────────────────
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * (buttonConfig.gapAboveToolbar ?? 0.075),
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.55,
          y: -dimensions.height / 2 + buttonConfig.height / 1.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.065,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.415,
          y: -dimensions.height / 2 + buttonConfig.height / 1.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width / 1.4,
          y: toolbar.height - buttonConfig.height / 1.7,
        };
      }
    } else {
      const yOffset = 0.08;
      return {
        x: toolbar.width - buttonConfig.width * 1.285,
        y: uiSymbolSize * yOffset,
      };
    }
  }, [
    buttonConfig.gapAboveToolbar,
    device, orientation, isMobileSmall, dimensions.height,
    toolbar.width, toolbar.height, toolbar.visualHeight,
    buttonConfig.width, buttonConfig.height, uiSymbolSize,
  ]);

  // ── Icon Texture ──────────────────────────────────────────────────────
  const iconTexture = getNewUITexture("icon_play");

  // ── Hover Effects (secondary theme: brightness via shared hook) ────────────
  const handlePointerOver = useCallback(() => {
    isHoveredRef.current = true;
    brightnessOver();
  }, [brightnessOver]);

  const handlePointerOut = useCallback(() => {
    isHoveredRef.current = false;
    brightnessOut();
  }, [brightnessOut]);

  // ── Action Handler ────────────────────────────────────────────────────────────
  const onPointerTap = useCallback(() => {
    if (!isInteractive && !isFeatureAnimating()) {
      return;
    }

    // Rate limiting
    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) {
      return;
    }
    lastActionTime = now;

    // Priority 0: Start autoplay (first click after entry completes — nothing animating)
    if (
      hasRespinsRemaining &&
      !autoplayStartedRef.current &&
      featureSpinComplete &&
      !featureSpinActive &&
      !isFeatureAnimating()
    ) {
      // Press animation: scale pulse only (context handles rotation on spin start)
      if (playButtonRef.current) {
        const sprite = playButtonRef.current;
        anime.remove(sprite.scale);
        const origX = sprite.scale.x;
        const origY = sprite.scale.y;
        anime({
          targets: sprite.scale,
          x: origX * 0.93,
          y: origY * 0.93,
          duration: 142,
          easing: "easeOutQuad",
          complete: () => {
            anime({
              targets: sprite.scale,
              x: origX,
              y: origY,
              duration: 180,
              easing: "easeOutQuad",
            });
          },
        });
      }
      soundManager.play(SOUND_NAME.button_click);
      startAutoplay();
      return;
    }

    // Priority 1: Feature animations in progress → slam stop
    if (isFeatureAnimating()) {
      // EOB collection: always allow slam stop (featureSpinComplete=true during EOB)
      // Entry spawn: always allow slam stop (coins spawning 1-by-1, player can speed up)
      // Regular features: gated by SLAM_STOP_BUTTON_ENABLED config
      const isEOB = featureSpinComplete;
      const isEntry = !featureSpinActive && !featureSpinComplete;
      if (isEOB || isEntry || FEATURES_CONFIG.POT_COLLECTION.FEATURE_SPEED?.SLAM_STOP_BUTTON_ENABLED) {
        soundManager.play(SOUND_NAME.button_click);
        requestSlamStop();
      }
    } else if (hasRespinsRemaining && featureSpinActive && !featureSpinComplete) {
      // Reels still spinning → manual stop
      // Allow repeat calls — engine handles tension stop on second press
      // (first press stops non-tension cells, second press stops tension cell)
      soundManager.play(SOUND_NAME.button_click);
      if (!stopDispatched) {
        setStopDispatched(true);
      }
      handleManualStop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInteractive, featureSpinActive, featureSpinComplete, hasRespinsRemaining,
    handleManualStop, startAutoplay,
    requestSlamStop, isFeatureAnimating, autoplayStartedRef,
  ]);

  // ── Spacebar Support (RC pattern — simple tap only) ───────────────────
  useEffect(() => {

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) return;
      if (event.repeat) return;
      // Guard against effect re-registration resetting browser repeat tracking
      if (spacePressedRef.current) return;
      spacePressedRef.current = true;

      event.preventDefault();

      const now = Date.now();
      lastActionTime = now;

      const state = store.getState();
      const pcActive = state.potCollection?.potCollectionActive;
      const transitionActive = state.animations?.potCollectionTransitionActive;


      if (pcActive) {
        if (state.animations.criticalAnimationPlaying) {
          return;
        }

        // Priority 0: Start autoplay (first press after entry completes)
        if (!autoplayStartedRef.current) {
          const spinDone = typeof state.spin.featureSpinComplete === 'boolean'
            ? state.spin.featureSpinComplete
            : state.spin.featureSpinComplete?.complete ?? state.spin.featureSpinComplete;
          const isSpinning = state.spin.featureSpinActive;
          const respins = state.potCollection?.respinsLeft ?? 0;

          if (respins > 0 && spinDone && !isSpinning && !isFeatureAnimating()) {
            // Context handles rotation on spin start — just trigger autoplay
            soundManager.play(SOUND_NAME.button_click);
            startAutoplay();
            return;
          }
        }

        // Priority 1: Feature animations in progress → slam stop
        // Works even during transition — lets player speed up intro coin spawning
        if (isFeatureAnimating()) {
          // EOB collection: always allow slam stop
          // Entry spawn: always allow slam stop (coins spawning 1-by-1, player can speed up)
          // Regular features: gated by SLAM_STOP_BUTTON_ENABLED config
          const spinDoneNow = typeof state.spin.featureSpinComplete === 'boolean'
            ? state.spin.featureSpinComplete
            : state.spin.featureSpinComplete?.complete ?? state.spin.featureSpinComplete;
          const isEOB = spinDoneNow;
          const isEntry = !state.spin.featureSpinActive && !spinDoneNow;
          const respins = state.potCollection?.respinsLeft ?? 0;
          if (isEOB || isEntry || (respins > 0 && FEATURES_CONFIG.POT_COLLECTION.FEATURE_SPEED?.SLAM_STOP_BUTTON_ENABLED)) {
            soundManager.play(SOUND_NAME.button_click);
            requestSlamStop();
          }
          return;
        }

        // All other actions (spin, manual stop) require transition to be finished
        if (transitionActive) {
          return;
        }

        const isSpinning = state.spin.featureSpinActive;
        const spinDone = typeof state.spin.featureSpinComplete === 'boolean'
          ? state.spin.featureSpinComplete
          : state.spin.featureSpinComplete?.complete ?? state.spin.featureSpinComplete;


        if (isSpinning && !spinDone && (state.potCollection?.respinsLeft ?? 0) > 0) {
          soundManager.play(SOUND_NAME.button_click);
          setStopDispatched(true);
          handleManualStop();
        }
      }
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) return;
      spacePressedRef.current = false;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    handleManualStop, startAutoplay,
    requestSlamStop, isFeatureAnimating,
    playButtonRef, autoplayStartedRef,
  ]);

  // Sync brightness hook ref with playButtonRef and cleanup on unmount
  useEffect(() => {
    brightnessRef.current = playButtonRef.current;
    return () => {
      cleanupFilters();
    };
  }, [playButtonRef, brightnessRef, cleanupFilters]);

  // Expose show/hide on pcPlayCounterRef for the respin counter controller
  useEffect(() => {
    pcPlayCounterRef.current = {
      show(value) {
        setCounterValue(String(value));
        setCounterVisible(true);
      },
      hide() {
        setCounterVisible(false);
      },
    };
    return () => {
      pcPlayCounterRef.current = null;
    };
  }, [pcPlayCounterRef]);

  // Counter text style — Noto Sans bold, white, sized relative to button
  const counterTextStyle = useMemo(() => new PIXI.TextStyle({
    fontFamily: '"Noto Sans", sans-serif',
    fontSize: Math.round(buttonConfig.width * 0.32),
    fontWeight: '800',
    fill: 0xffffff,
    align: 'center',
  }), [buttonConfig.width]);

  return (
    <Container
      name="button_pot_collection_play"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      {/* Interactive circular area — secondary theme pattern */}
      <Graphics
        name="pc_play_interactive_area"
        eventMode="static"
        cursor="pointer"
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        onpointertap={onPointerTap}
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={playButtonRef}
        texture={iconTexture}
        name="button_pot_collection_play_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={1}
        resolution={2}
      />

      {/* Respin counter number — controlled imperatively via pcPlayCounterRef */}
      <Text
        text={counterValue}
        style={counterTextStyle}
        anchor={0.5}
        x={0}
        y={0}
        zIndex={1}
        eventMode="none"
        roundPixels={true}
        resolution={2}
        visible={counterVisible}
      />
    </Container>
  );
};

export default ButtonPotCollectionPlay;
